/**
 * PathwaySEO — <head> metadata for the two `/pathways/*` explainer
 * pages. Renders title + description + Open Graph + Twitter card
 * tags via react-helmet-async so social-share previews (WhatsApp,
 * iMessage, Slack, Twitter/X) render cleanly when a parent drops
 * one of these links into a group chat.
 *
 * Matches the `SEOHead` prop shape used elsewhere in the app so the
 * two are drop-in interchangeable. The one wrinkle handled here is
 * that `index.html` ships a static `og:title` fallback for the
 * whole app — react-helmet-async correctly replaces it on client
 * navigation, but the initial paint sometimes shows both until
 * Helmet commits, hence the deferred cleanup below.
 */
import { Helmet } from "react-helmet-async";

export default function PathwaySEO({
  title,
  description,
  path = "",
  image,
  siteName = "WhichUniversityAdvisor.com",
}) {
  const origin =
    (typeof window !== "undefined" && window.__WUA_CANONICAL_ORIGIN__) ||
    (typeof window !== "undefined" && window.location.origin) ||
    "https://whichuniversityadvisor.com";
  const url = `${origin}${path}`;
  const fullTitle = `${title} · ${siteName}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
    </Helmet>
  );
}
